import { Middleware, MiddlewareAPI } from "redux"

import { isSentryActive, logBreadcrumb } from "../analytics"
import { ApiResponseError, parseApiError } from "../api/apiUtils"
import { isDeviceOffline, isNetworkError } from "../api/utils"
import { HTTP_STATUS } from "../constants"
import { detectLockedFeature } from "../fetch"
import { setIsOffline } from "./app/appSlice"
import { clearAuth } from "./auth/authSlice"
import { setHasPaid } from "./payment/paymentSlice"
import { PAYMENT_REQUIRED_ERROR } from "./tablet/constants"
import { isFulfilled, isRejected, isRejectedWithValue } from "@reduxjs/toolkit"
import { captureException } from "@sentry/react"

type ParsedReduxError = {
  message?: string
}

type RejectedAction = {
  type: string
  meta?: { arg?: unknown }
}

// Those errors are expected and we are using them to set app state
const INFO_ERRORS = [
  "Authentication credentials were not provided.",
  "Payment required for functionalities to work.",
  PAYMENT_REQUIRED_ERROR,
]

/**
 * RTK Query endpoints and thunks share one middleware, so the endpoint name
 * (when there is one) is the most specific label for grouping in Sentry.
 */
const getActionLabel = ({ type, meta }: RejectedAction): string =>
  (meta?.arg as { endpointName?: string } | undefined)?.endpointName ?? type

/**
 * Backend messages arrive translated (`?lang=`), so Sentry would open one
 * issue per language for the same failure. Group by where it failed instead
 * of by what it said.
 */
const reportRejection = (
  action: RejectedAction,
  message: string,
  extra: Record<string, unknown> = {},
) => {
  const label = getActionLabel(action)

  captureException(new Error(`${label}: ${message}`), {
    fingerprint: [
      "redux-rejection",
      label,
      String(extra.status ?? extra.errorName ?? ""),
    ],
    extra: { actionType: action.type, ...extra },
  })
}

export const sentryMiddleware: Middleware =
  (api: MiddlewareAPI) => (next) => (action) => {
    try {
      if (!isSentryActive || isFulfilled(action)) {
        return next(action)
      }

      if (isRejectedWithValue(action)) {
        const { payload } = action
        const apiError = parseApiError(payload) as ParsedReduxError
        const status =
          typeof payload === "object" && payload !== null && "status" in payload
            ? payload.status
            : undefined

        logBreadcrumb("Redux action failed (middleware rejected with value)")

        if (apiError && apiError.message) {
          logBreadcrumb(`Error: ${apiError.message}`)
        }

        /**
         * Only RTK Query rejections carry a status. Legacy thunks reject with a
         * value when they have already handled the failure (form errors,
         * toasts), so they stay as breadcrumbs. 4xx responses and connectivity
         * failures (reshaped to status 0) are expected outcomes, not bugs. A
         * 2xx with an unparsable body skips reshaping and keeps RTK's
         * `PARSING_ERROR` status, which points at a proxy or backend fault.
         */
        const isServerFailure =
          status === "PARSING_ERROR" ||
          (typeof status === "number" &&
            status >= HTTP_STATUS.INTERNAL_SERVER_ERROR)

        if (!isServerFailure) {
          return next(action)
        }

        const { url, errorCode } = payload as ApiResponseError

        reportRejection(
          action,
          (apiError && apiError.message) || `HTTP ${status}`,
          { status, url, errorCode },
        )

        return next(action)
      }

      if (isRejected(action)) {
        const { error, meta, type } = action

        /*
         * RTK-query rejects with a ConditionError when the query is set to be skipped
         */
        if ("name" in error && error.name === "ConditionError") {
          return next(action)
        }

        // Unsubscribed queries and superseded thunks are aborted on purpose
        if (meta?.aborted || error.name === "AbortError") {
          return next(action)
        }

        /*
         * we are fetching employee screening data every time the user check-ins
         * to check if the user has a screening the endpoint returns 404 if the user
         * doesn't have a screening so we are ignoring this error
         */
        if (
          type === "api/executeQuery/rejected" &&
          getActionLabel(action) === "fetchEmployeeScreening"
        ) {
          return next(action)
        }

        logBreadcrumb("Redux action failed (middleware rejected)")

        if (error.message) {
          logBreadcrumb(`Error: ${error.message}`)
        }

        /*
         * we are using those errors to set app state, and connectivity
         * failures are already handled by the offline overlay
         */
        if (
          INFO_ERRORS.includes(error.message ?? "") ||
          isNetworkError(error)
        ) {
          return next(action)
        }

        reportRejection(action, error.message || "Unknown error", {
          errorName: error.name,
          errorCode: error.code,
          stack: error.stack,
        })

        return next(action)
      }

      return next(action)
    } catch (error) {
      console.error("Sentry middleware error", error)
      return next(action)
    }
  }

/**
 * JOAN-10552 / JOAN-11550: surfaces a global "no connection" overlay when API
 * calls fail while the device is actually offline. RTK Query / thunk failures
 * carry `FETCH_ERROR` / "Failed to fetch", which `isNetworkError` detects, but
 * that signal also fires for CORS, aborted uploads, and BE proxy timeouts —
 * so we also require `isDeviceOffline()` before taking over the page. The flag
 * is cleared when a request succeeds and the device is online again — a local
 * thunk can still fulfill from cache while the browser stays offline.
 */
export const offlineMiddleware: Middleware =
  ({ dispatch, getState }: MiddlewareAPI) =>
  (next) =>
  (action) => {
    try {
      const isOffline = (getState() as { app: { isOffline: boolean } }).app
        .isOffline

      if (isFulfilled(action)) {
        if (isOffline && !isDeviceOffline()) {
          dispatch(setIsOffline(false))
        }

        return next(action)
      }

      if (!isOffline && isDeviceOffline()) {
        if (isRejectedWithValue(action) && isNetworkError(action.payload)) {
          dispatch(setIsOffline(true))
        } else if (isRejected(action) && isNetworkError(action.error)) {
          dispatch(setIsOffline(true))
        }
      }

      return next(action)
    } catch (error) {
      console.error("Offline middleware error", error)
      return next(action)
    }
  }

export const apiErrorHandlingMiddleware: Middleware =
  ({ dispatch }: MiddlewareAPI) =>
  (next) =>
  (action) => {
    try {
      if (!isRejectedWithValue(action) && !isRejected(action)) {
        return next(action)
      }

      // Handle isRejected actions (e.g., from RTK Query)
      if (isRejected(action)) {
        // Check if it's a 401 error even in rejected actions
        const error = action.error as any
        const meta = action.meta as any
        const payload = action.payload as any

        // RTK Query can store status in multiple places depending on how the error was thrown
        const status =
          meta?.baseQueryMeta?.response?.status ||
          error?.status ||
          payload?.status

        if (status === HTTP_STATUS.UNAUTHORIZED) {
          dispatch(clearAuth())
        }

        // 402 marks the account unpaid; detectLockedFeature only refines which feature to lock
        if (status === HTTP_STATUS.PAYMENT_REQUIRED) {
          dispatch(
            setHasPaid(detectLockedFeature((payload as { url: string }).url)),
          )
        }

        return next(action)
      }

      // Handle isRejectedWithValue actions (e.g., from old thunks)
      const { payload } = action as any
      const apiError = parseApiError(payload) as ParsedReduxError

      if (!apiError) {
        return next(action)
      }

      const { status } = payload

      if (status === HTTP_STATUS.UNAUTHORIZED) {
        dispatch(clearAuth())
        return next(action)
      }

      if (status === HTTP_STATUS.PAYMENT_REQUIRED) {
        dispatch(
          setHasPaid(detectLockedFeature((payload as { url: string }).url)),
        )
        return next(action)
      }

      return next(action)
    } catch (error) {
      console.error("API error handling middleware error", error)
      return next(action)
    }
  }
